import { Injectable } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { forkJoin, Observable, Subject, tap } from 'rxjs';

import { Dao } from '../dao';
import {
  AssuranceEmprunteurContratDto,
  MutuelleContratDto,
  PREVOYANCE_TYPE_CONFIG,
  PrevoyanceContratDto,
  PrevoyanceType,
} from './prevoyance.types';

@Injectable({ providedIn: 'root' })
export class PrevoyanceDao extends Dao {
  private resetSubject = new Subject<void>();
  onReset = this.resetSubject.asObservable();

  constructor(
    httpApi: HttpApiService,
    authentificationService: AuthentificationService,
  ) {
    super(httpApi, authentificationService);
  }

  getContrats<T>(accountId: number, type: PrevoyanceType): Observable<T[]> {
    const path = PREVOYANCE_TYPE_CONFIG[type].path;
    return this.httpApi.get(
      `/accounts/${accountId}/prevoyance/${path}/contrats`,
    );
  }

  createContrat(
    accountId: number,
    type: PrevoyanceType,
    body: any,
  ): Observable<any> {
    const path = PREVOYANCE_TYPE_CONFIG[type].path;
    return this.httpApi
      .post(`/accounts/${accountId}/prevoyance/${path}/contrats`, body)
      .pipe(tap(() => this.resetSubject.next()));
  }

  updateContrat(
    accountId: number,
    type: PrevoyanceType,
    contratId: number,
    body: any,
  ): Observable<any> {
    const path = PREVOYANCE_TYPE_CONFIG[type].path;
    return this.httpApi
      .put(
        `/accounts/${accountId}/prevoyance/${path}/contrats/${contratId}`,
        body,
      )
      .pipe(tap(() => this.resetSubject.next()));
  }

  deleteContrat(
    accountId: number,
    type: PrevoyanceType,
    contratId: number,
  ): Observable<any> {
    const path = PREVOYANCE_TYPE_CONFIG[type].path;
    return this.httpApi
      .delete(
        `/accounts/${accountId}/prevoyance/${path}/contrats/${contratId}`,
      )
      .pipe(tap(() => this.resetSubject.next()));
  }

  getContratById<T>(
    accountId: number,
    type: PrevoyanceType,
    contratId: number,
  ): Observable<T> {
    const path = PREVOYANCE_TYPE_CONFIG[type].path;
    return this.httpApi.get(
      `/accounts/${accountId}/prevoyance/${path}/contrats/${contratId}`,
    );
  }

  linkDocumentToContrat(
    accountId: number,
    type: PrevoyanceType,
    contratId: number,
    gedDocumentId: number,
  ): Observable<any> {
    const path = PREVOYANCE_TYPE_CONFIG[type].path;
    return this.httpApi
      .post(
        `/accounts/${accountId}/prevoyance/${path}/contrats/${contratId}/documents/${gedDocumentId}`,
        {},
      )
      .pipe(tap(() => this.resetSubject.next()));
  }

  removeDocumentFile(
    accountId: number,
    type: PrevoyanceType,
    contratId: number,
    gedDocumentId: number,
    fileIndex: number,
  ): Observable<any> {
    const path = PREVOYANCE_TYPE_CONFIG[type].path;
    return this.httpApi.delete(
      `/accounts/${accountId}/prevoyance/${path}/contrats/${contratId}/documents/${gedDocumentId}/files/${fileIndex}`,
    );
  }

  getBaremeInvaliditeRef(): Observable<any[]> {
    return this.httpApi.get('/ref/bareme-invalidite');
  }

  getContratDocuments(
    accountId: number,
    type: PrevoyanceType,
    contratId: number,
  ): Observable<any[]> {
    const path = PREVOYANCE_TYPE_CONFIG[type].path;
    return this.httpApi.get(
      `/accounts/${accountId}/prevoyance/${path}/contrats/${contratId}/documents`,
    );
  }

  downloadDocumentFile(
    accountId: number,
    type: PrevoyanceType,
    contratId: number,
    gedDocumentId: number,
    fileIndex: number,
  ): Observable<ArrayBuffer> {
    const path = PREVOYANCE_TYPE_CONFIG[type].path;
    return this.httpApi.getWithOptions(
      `/accounts/${accountId}/prevoyance/${path}/contrats/${contratId}/documents/${gedDocumentId}/files/${fileIndex}`,
      { responseType: 'arraybuffer' },
    );
  }

  getAllContrats(accountId: number) {
    return forkJoin({
      ae: this.getContrats<AssuranceEmprunteurContratDto>(
        accountId,
        'assurance-emprunteur',
      ),
      mutuelle: this.getContrats<MutuelleContratDto>(accountId, 'mutuelle'),
      prevoyance: this.getContrats<PrevoyanceContratDto>(
        accountId,
        'prevoyance',
      ),
    });
  }

  onUserChange() {
    this.resetSubject?.next();
  }
}
